import { G } from '@mobily/ts-belt';
import {
  SupportedAudioFormat,
  formatMaybeErrorForLogging,
  hasRequiredAudioMetrics,
  getAudioMetricsErrors,
  GetAudioMetricsResponse,
  mapErrorToGoogleDocLink,
  ErrorType,
  createAwsTranscriptionService,
  createWhisperTranscriptionService,
} from '@scale/mm-shared';
import { Icon, Spinner, Typography } from '@scale/scaleui';
import {
  AudioRecordingAnnotation,
  AudioRecordingField,
} from '@scale/shared/types/params/textcollection';
import { AudioRecordingFieldAnnotation } from '@scale/shared/types/response/textcollection';
import { produce } from 'immer';
import fetch from 'isomorphic-fetch';
import { useContext, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { ChatAudioEditor } from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/audio-attachments/ChatAudioEditor';
import { ChatAudioTranscript } from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/audio-attachments/ChatAudioTranscript';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { ChatAudioEditableCombined } from 'react-scale/components/scalers/tasks/textcollection/AudioRecording/ChatAudioEditableCombined';
import FieldHeader from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/FieldHeader';
import useCDSMultipartUpload from 'react-scale/components/scalers/tasks/textcollection/hooks/useCDSMultipartUpload';
import {
  getAudioSource,
  processAudioAttachment,
  getAudioMetrics,
} from 'react-scale/lib/chat-api/multimodal';
import logger from 'react-scale/lib/logging';
import retry from '@scale/shared/utils/retry';
import _ from 'lodash';
import { Alert } from 'design-system';
import { AudioValidationError } from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/audio-attachments/wavesurfer-utils';
import apiFetch from 'react-scale/lib/api-fetch';
import { ReferenceAudioClip } from './ReferenceAudioClip';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
/**
 * ensure that we have no vertical jump in our UI
 */
const AUDIO_SECTION_MIN_HEIGHT = 532;
const DISABLED_AUDIO_SECTION_MIN_HEIGHT = 200;

type Props = {
  annotation: AudioRecordingFieldAnnotation;
  disabled?: boolean;
  field: AudioRecordingField;
  setAnnotation: (annotation: AudioRecordingFieldAnnotation) => void;
};

// eslint-disable-next-line complexity
export const AudioRecordingFieldSection = ({
  annotation,
  disabled,
  field,
  setAnnotation,
}: Props) => {
  const { assignmentId, projectId, workerId } = useContext(ChatTaskContext);
  const { upload } = useCDSMultipartUpload(assignmentId, workerId);
  disabled = disabled || field.disableRecording;

  // Will remove this once we support showing multiple errors
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [audioErrors, setAudioErrors] = useState<Array<AudioValidationError>>([]);

  const shouldUseWhisperTranscription = useUnleashFeatureFlag(
    'chat-v1-use-whisper-transcription',
    { projectId },
    false,
  );

  /**
   * fix for missing format in some old tasks
   */
  const fieldFormat: SupportedAudioFormat = field.format || 'wav';

  const getAnnotation = (
    annotation: AudioRecordingAnnotation,
    audioMetrics: GetAudioMetricsResponse | undefined,
  ) => {
    return {
      ...annotation,
      // add values to the response so we can grade them in benchmarks, autoreviewer, etc
      ..._.omitBy(
        {
          ...audioMetrics?.volumeMetrics,
          bgNoiseScore: audioMetrics?.speechQualityScores?.bgNoise,
          overallSpeechScore: audioMetrics?.speechQualityScores?.overall,
          // TODO: add in transcriptUrl, etc too
        },
        _.isUndefined,
      ),
    };
  };

  const {
    mutate: handleFinishRecording,
    isLoading: isFinishingRecording,
    isError: isFinishRecordingError,
  } = useMutation(
    async ({
      recordedAudio,
    }: {
      recordedAudio: { blob: Blob; duration?: number } | null;
    }): Promise<AudioRecordingAnnotation | null> => {
      if (recordedAudio === null) {
        return null;
      }

      const fileName = `audio.${fieldFormat}`;
      const mimeType = `audio/${fieldFormat}`;

      // using the desired mime type since CDS doesn't have a way to change that after conversion actually happens (yet)
      const file = new File(
        [recordedAudio.blob], // fileBits
        fileName,
        {
          type: mimeType,
        },
      );

      // CDS multipart upload also converts file format
      // todo: add from/to formats
      const { cdsUrl } = await upload({
        file,
        fileType: fieldFormat,
      });

      try {
        const entity = await retry(
          () =>
            processAudioAttachment({
              audioUrl: cdsUrl,
              audioFormat: fieldFormat,
              assignmentId,
              workerId,
              userGeneratedAudio: true,
              transcriptionService: shouldUseWhisperTranscription
                ? createWhisperTranscriptionService()
                : createAwsTranscriptionService(),
            }),
          { limit: 3 },
        );

        return {
          url: entity.url,
          mimeType,
          name: fileName,
          audioProcessingWorkflowId: entity.processingWorkflowId,
          duration: recordedAudio.duration,
        };
      } catch (error) {
        logger.error(`AudioRecordingFieldSection: Failed to start processing audio attachment`, {
          projectId,
          assignmentId,
          workerId,
          error: formatMaybeErrorForLogging(error),
        });
        // fallback if no processing was kicked off
        return {
          url: cdsUrl,
          mimeType,
          name: fileName,
        };
      }
    },
    {
      onSuccess: async audioAnnotation => {
        // TODO: pull audio volume configs from field first, then the global audio config
        if (hasRequiredAudioMetrics(undefined, field) || audioAnnotation === null) {
          // don't fetch audio metrics if there are no validation configs set
          setAnnotation(
            produce(annotation, draft => {
              draft.response = audioAnnotation === null ? [] : [audioAnnotation];
            }),
          );
        } else {
          const audioUrl = audioAnnotation.url;
          let metadata: GetAudioMetricsResponse | undefined = undefined;
          try {
            metadata = await retry(() => getAudioMetrics({ audioUrl }), { limit: 3 });
          } catch (error) {
            logger.error(`AudioRecordingFieldSection: Failed to get audioMetrics`, {
              projectId,
              assignmentId,
              workerId,
              error: formatMaybeErrorForLogging(error),
            });
          }
          setAnnotation(
            produce(annotation, draft => {
              draft.response = [getAnnotation(audioAnnotation, metadata)];
            }),
          );
        }
      },
      onError: error => {
        logger.error('AudioRecordingFieldSection: Failed to upload audio recording', {
          projectId,
          assignmentId,
          workerId,
          error: formatMaybeErrorForLogging(error),
        });
      },
    },
  );

  const currentResponse: AudioRecordingAnnotation | undefined = annotation.response[0];
  const currentResponseUrl: string | undefined = currentResponse?.url;

  const { data: audioMetrics, isError: isGetAudioMetricsError } = useQuery(
    ['getAudioMetrics', currentResponse],
    async (): Promise<GetAudioMetricsResponse | null> => {
      if (!currentResponse) {
        return null;
      }
      const audioUrl = currentResponse.url;
      // first try grabbing the data from the annotation
      let metadata: GetAudioMetricsResponse = {
        volumeMetrics: currentResponse,
        speechQualityScores: {
          bgNoise: currentResponse?.bgNoiseScore,
          overall: currentResponse?.overallSpeechScore,
        },
      };
      if (!hasRequiredAudioMetrics(metadata, field)) {
        metadata = await getAudioMetrics({ audioUrl });
      }
      return metadata;
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 3,
      retryDelay: attemptIndex => Math.min(30_000, Math.pow(1.1, attemptIndex) * 2_000),
    },
  );

  const {
    data: referenceAudioFileBlob,
    isLoading: isReferenceAudioFileBlobLoading,
    isError: isReferenceAudioFileBlobError,
  } = useQuery(
    ['getReferenceAudioFileBlob', field.referenceAudioFile ?? ''],
    async (): Promise<Blob | null> => {
      if (!field.referenceAudioFile) {
        return null;
      }

      const { source } = await getAudioSource({ audioUrl: field.referenceAudioFile });
      const data = await fetch(source);

      if (!data.ok) {
        throw new Error('Failed to fetch audio file');
      }

      return await data.blob();
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const {
    data: currentResponseBlob,
    isLoading: isCurrentResponseBlobLoading,
    isError: isCurrentResponseBlobError,
  } = useQuery(
    ['getAudioBlob', currentResponseUrl ?? ''],
    async (): Promise<Blob | null> => {
      if (!currentResponseUrl) {
        return null;
      }
      let urlSource;
      if (currentResponseUrl.includes('scale-audio-collector')) {
        // hack for goldfish audio recording
        const { audioUrl } = await apiFetch(null, `internal/genai/invokeChatTool`, {
          method: 'POST',
          body: {
            toolId: 'goldfish_audio_get_recording',
            params: {
              id: currentResponseUrl.split('?')[0].split('/')[
                currentResponseUrl.split('?')[0].split('/').length - 1
              ],
            },
            taskMetadta: {},
          },
        });
        urlSource = audioUrl;
      } else {
        const { source } = await getAudioSource({ audioUrl: currentResponseUrl });
        urlSource = source;
      }

      const data = await fetch(urlSource);

      if (!data.ok) {
        throw new Error('Failed to fetch audio blob');
      }

      return await data.blob();
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const isValidating =
    currentResponse && !hasRequiredAudioMetrics(audioMetrics, field) && !isGetAudioMetricsError;

  // disabled
  if (disabled) {
    return (
      <div className="flex flex-col gap-4">
        <FieldHeader title={field.title} description={field.description} hint={field.hint} />
        <ReferenceAudioClip
          referenceAudioFileBlob={referenceAudioFileBlob}
          isReferenceAudioFileBlobLoading={isReferenceAudioFileBlobLoading}
          isReferenceAudioFileBlobError={isReferenceAudioFileBlobError}
          setAudioErrors={setAudioErrors}
        />
        <div
          className="flex flex-col gap-4"
          style={{
            minHeight: field.disableRecording
              ? DISABLED_AUDIO_SECTION_MIN_HEIGHT
              : AUDIO_SECTION_MIN_HEIGHT,
          }}
        >
          {isCurrentResponseBlobLoading ? (
            <div className="flex gap-1 items-center justify-center">
              <Spinner size="xs" variant="neutral" />
              <Typography.Paragraph3>Loading...</Typography.Paragraph3>
            </div>
          ) : isCurrentResponseBlobError ? (
            <div className="flex gap-1 items-center justify-center">
              <Icon icon={['fas', 'times-circle']} variant="danger" />
              <Typography.Paragraph3 color="text-danger-500">
                Failed to load the audio recording
              </Typography.Paragraph3>
            </div>
          ) : G.isNotNullable(currentResponseBlob) ? (
            <>
              <Typography.Overline2>Audio clip</Typography.Overline2>
              <ChatAudioEditor readonly originalBlob={currentResponseBlob} />
              {/* This is temporary since this flow with disable recording will generate a transcript separately */}
              {!field.disableRecording && (
                <ChatAudioTranscript audioUrl={currentResponseUrl} userGeneratedAudio={true} />
              )}
              {getAudioMetricsErrors(audioMetrics, field).length > 0 && (
                <div className="flex flex-col gap-1">
                  {getAudioMetricsErrors(audioMetrics, field).map((error, index) => (
                    <div key={index} className="flex items-center gap-2 w-full">
                      <Alert severity="warning" className="flex-grow">
                        {error}
                      </Alert>
                      {mapErrorToGoogleDocLink(error as ErrorType) && (
                        <a
                          href={mapErrorToGoogleDocLink(error as ErrorType)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0"
                        >
                          <Icon icon={['fas', 'question-circle']} variant="warning" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <Typography.Overline2>No audio</Typography.Overline2>
          )}
        </div>
      </div>
    );
  }

  // enabled
  return (
    <div className="flex flex-col gap-4">
      <FieldHeader title={field.title} description={field.description} hint={field.hint} />

      <div className="flex flex-col gap-2" style={{ minHeight: AUDIO_SECTION_MIN_HEIGHT }}>
        <ReferenceAudioClip
          referenceAudioFileBlob={referenceAudioFileBlob}
          isReferenceAudioFileBlobLoading={isReferenceAudioFileBlobLoading}
          isReferenceAudioFileBlobError={isReferenceAudioFileBlobError}
          setAudioErrors={setAudioErrors}
        />

        <ChatAudioEditableCombined
          currentResponseBlob={currentResponseBlob ?? null}
          currentResponseUrl={currentResponseUrl ?? null}
          onFinish={handleFinishRecording}
          maxAudioDuration={field.maxAudioDuration}
          minAudioDuration={field.minAudioDuration}
          isTextCollectionStep
        />

        {(isCurrentResponseBlobLoading || isFinishingRecording || isValidating) && (
          <div className="flex gap-1 items-center justify-center">
            <Spinner size="xs" variant="neutral" />
            <Typography.Paragraph3>
              {isFinishingRecording
                ? 'Processing...'
                : isCurrentResponseBlobLoading
                ? 'Loading...'
                : 'Validating...'}
            </Typography.Paragraph3>
          </div>
        )}

        {isCurrentResponseBlobError && (
          <div className="flex gap-1 items-center justify-center">
            <Icon icon={['fas', 'times-circle']} variant="danger" />
            <Typography.Paragraph3 color="text-danger-500">
              Failed to load the audio recording
            </Typography.Paragraph3>
          </div>
        )}

        {isFinishRecordingError && (
          <div className="flex gap-1 items-center justify-center">
            <Icon icon={['fas', 'times-circle']} variant="danger" />
            <Typography.Paragraph3 color="text-danger-500">
              Failed to process recording
            </Typography.Paragraph3>
          </div>
        )}
        {getAudioMetricsErrors(audioMetrics, field).length > 0 && (
          <div className="flex flex-col gap-1">
            {getAudioMetricsErrors(audioMetrics, field).map((error, index) => (
              <div key={index} className="flex items-center gap-2 w-full">
                <Alert severity="warning" className="flex-grow">
                  {error}
                </Alert>
                {mapErrorToGoogleDocLink(error as ErrorType) && (
                  <a
                    href={mapErrorToGoogleDocLink(error as ErrorType)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0"
                  >
                    <Icon icon={['fas', 'question-circle']} variant="warning" />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
